import {
    Component,
    ElementRef,
    HostListener,
    Input,
    Output,
    EventEmitter,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime } from 'rxjs';
import { OpcionesSelect } from './interface/opciones-select.interface';

/**
 * Componente de icono de filtro reutilizable
 *
 * Este componente proporciona una interfaz de filtro desplegable que puede ser
 * de tipo input o select, con soporte para debounce y eventos personalizados.
 *
 * @example
 * ```html
 * <!-- Filtro de tipo input -->
 * <app-icono-filtro
 *   tipo="input"
 *   placeholder="Buscar..."
 *   (filtrar)="onFiltrar($event)"
 *   [debounceTime]="300">
 * </app-icono-filtro>
 *
 * <!-- Filtro de tipo select -->
 * <app-icono-filtro
 *   tipo="select"
 *   [opciones]="['Opción 1', 'Opción 2']"
 *   (filtrar)="onFiltrar($event)">
 * </app-icono-filtro>
 * ```
 *
 * @remarks
 * - Soporta debounce automático para inputs
 * - Cierre automático al hacer clic fuera
 * - Posicionamiento dinámico del popup
 * - Compatible con formularios reactivos y template-driven
 *
 * @publicApi
 */
@Component({
    selector: 'app-icono-filtro',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './icono-filtro.component.html',
    styleUrls: ['./icono-filtro.component.css'],
})
export class IconoFiltroComponent implements OnInit, OnDestroy {
    /**
     * Tipo de filtro a mostrar
     *
     * @default 'input'
     * @remarks
     * - 'input': Muestra un campo de texto para búsqueda
     * - 'number': Muestra un campo numérico
     * - 'select': Muestra un dropdown con opciones predefinidas
     * - 'multiselect': Muestra una lista con selección múltiple
     */
    @Input() tipo: 'input' | 'number' | 'select' | 'multiselect' = 'input';

    /** Valores seleccionados para el modo multiselect. */
    selectedValues: Set<string> = new Set();

    /**
     * Opciones disponibles para el filtro de tipo select
     *
     * @default []
     * @example
     * ```typescript
     * opciones = ['Todos', 'Activos', 'Inactivos'];
     * ```
     */
    @Input() opciones: OpcionesSelect[] = [];

    /**
     * Texto placeholder para el input
     *
     * @default 'Filtrar...'
     */
    @Input() placeholder = 'Filtrar...';

    /**
     * Valor actual del filtro
     *
     * @default ''
     * @remarks Soporta two-way data binding con `valueChange`
     */
    @Input() value = '';

    /**
     * Tiempo de debounce en milisegundos para el filtro de tipo input
     *
     * @default 500
     * @remarks
     * - Valor 0 desactiva el debounce
     * - Útil para evitar múltiples llamadas API durante la escritura
     */
    @Input() debounceTime = 500;

    /**
     * Evento emitido cuando se realiza un filtro
     *
     * @emits string - Valor del filtro aplicado
     * @remarks
     * - Para inputs: Se emite después del debounce o con Enter
     * - Para selects: Se emite inmediatamente al seleccionar
     */
    @Output() filtrar = new EventEmitter<string>();

    /**
     * Evento emitido cuando cambia el valor (two-way data binding)
     *
     * @emits string - Nuevo valor del filtro
     * @remarks Compatible con la sintaxis `[(value)]`
     */
    @Output() valueChange = new EventEmitter<string>();

    /**
     * Controla la visibilidad del popup de filtro
     *
     * @internal
     */
    mostrarFiltro = false;

    /**
     * Valor interno para el control del input
     *
     * @internal
     */
    inputValue = '';

    /**
     * Subject para manejar el debounce del input
     *
     * @internal
     */
    private readonly inputSubject = new Subject<string>();

    /**
     * Suscripción al subject para gestionar la destrucción
     *
     * @internal
     */
    private subscription: Subscription = new Subscription();

    /**
     * Crea una instancia del componente IconoFiltroComponent
     *
     * @param eRef - Referencia al elemento del componente para detectar clics externos
     */
    constructor(private readonly eRef: ElementRef) {}

    /**
     * Inicializa el componente
     *
     * @remarks
     * - Configura la suscripción al subject con debounce
     * - Inicializa el valor interno con el valor proporcionado
     *
     * @override
     */
    ngOnInit(): void {
        this.inputValue = this.value;
        this.inicializarMultiselectDesdeValor();

        this.subscription = this.inputSubject
            .pipe(debounceTime(this.debounceTime))
            .subscribe((valor) => {
                this.filtrar.emit(valor);
            });
    }

    /**
     * Limpia las suscripciones al destruir el componente
     *
     * @override
     */
    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Alterna la visibilidad del popup de filtro
     *
     * @remarks
     * - Calcula la posición del popup basándose en la ubicación del icono
     * - Establece variables CSS para el posicionamiento
     */
    toggleFiltro(): void {
        this.mostrarFiltro = !this.mostrarFiltro;

        if (this.mostrarFiltro) {
            const icono =
                this.eRef.nativeElement.querySelector('.filtro-wrapper');
            const rect = icono.getBoundingClientRect();

            // Guardamos la posición como variables CSS
            document.documentElement.style.setProperty(
                '--popup-top',
                `${rect.bottom + 8}px`
            );
            document.documentElement.style.setProperty(
                '--popup-left',
                `${rect.right - 180}px`
            );
        }
    }

    /**
     * Maneja los cambios en el input de tipo texto
     *
     * @param event - Evento del input
     * @remarks
     * - Actualiza el valor interno
     * - Emite valueChange para two-way binding
     * - Notifica al subject para el debounce
     */
    onInputChange(event: any): void {
        const valor = event.target.value;
        this.inputValue = valor;
        this.value = valor;
        this.valueChange.emit(valor);
        this.inputSubject.next(valor);
    }

    /**
     * Maneja los cambios en el select
     *
     * @param event - Evento del select
     * @remarks
     * - Emite los eventos inmediatamente
     * - No cierra el popup después de seleccionar
     */
    onSelectChange(valor: string): void {
        this.value = valor;
        this.valueChange.emit(valor);
        this.filtrar.emit(valor);
    }

    /**
     * Limpia el filtro actual
     *
     * @remarks
     * - Reinicia todos los valores
     * - Emite eventos con cadena vacía
     */
    limpiarFiltro(): void {
        this.inputValue = '';
        this.value = '';
        this.selectedValues.clear();
        this.valueChange.emit('');
        this.filtrar.emit('');
    }

    toggleOpcionMulti(id: string): void {
        if (this.selectedValues.has(id)) {
            this.selectedValues.delete(id);
        } else {
            this.selectedValues.add(id);
        }
        const valor = Array.from(this.selectedValues).join(',');
        this.value = valor;
        this.valueChange.emit(valor);
        this.filtrar.emit(valor);
    }

    private inicializarMultiselectDesdeValor(): void {
        if (this.tipo !== 'multiselect' || !this.value?.trim()) return;
        this.value
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
            .forEach((id) => this.selectedValues.add(id));
    }

    estaSeleccionado(id: string): boolean {
        return this.selectedValues.has(id);
    }

    /**
     * Maneja la tecla Enter en el input
     *
     * @param event - Evento del teclado
     * @remarks
     * - Realiza la búsqueda inmediatamente
     * - Cierra el popup después de buscar
     */
    onInputKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            const valor = (event.target as HTMLInputElement).value;
            this.filtrar.emit(valor);
            this.mostrarFiltro = false;
        }
    }

    /**
     * Cierra manualmente el popup de filtro
     */
    cerrarFiltro(): void {
        this.mostrarFiltro = false;
    }

    /**
     * Escucha clics fuera del componente para cerrar el popup
     *
     * @param event - Evento del mouse
     * @internal
     */
    @HostListener('document:click', ['$event'])
    onClickFuera(event: MouseEvent): void {
        if (!this.eRef.nativeElement.contains(event.target)) {
            this.mostrarFiltro = false;
        }
    }

    get tieneValor(): boolean {
        if (this.tipo === 'multiselect') return this.selectedValues.size > 0;
        if (this.tipo === 'select') return String(this.value ?? '').trim() !== '';
        return String(this.inputValue ?? '').trim() !== '';
    }
}
